import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  useGetFormVisibilityQuery,
  useUpdateFormVisibilityMutation,
} from '../../../../app/athenaApi';
import { useLazyGetApiFormsTypesQuery } from '../../../../app/athenaApiGen';
import { setAvailableForms } from '../../../../app/user/userStateSlice';

const FormVisibility = () => {
  const dispatch = useDispatch();
  const {
    data: config,
    isLoading,
    error: loadError,
  } = useGetFormVisibilityQuery();
  const [updateFormVisibility, { isLoading: isSaving }] =
    useUpdateFormVisibilityMutation();
  const [refetchAvailableForms] = useLazyGetApiFormsTypesQuery();

  const [formStates, setFormStates] = useState<Record<string, boolean>>({});

  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationSeverity, setNotificationSeverity] = useState<
    'success' | 'error'
  >('success');

  useEffect(() => {
    if (config?.forms) {
      const seeded: Record<string, boolean> = {};
      config.forms.forEach((f) => {
        seeded[f.formType] = f.enabled;
      });
      setFormStates(seeded);
    }
  }, [config]);

  const showNotification = (
    message: string,
    severity: 'success' | 'error' = 'success',
  ) => {
    setNotificationMessage(message);
    setNotificationSeverity(severity);
    setNotificationOpen(true);
  };

  const handleNotificationClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') return;
    setNotificationOpen(false);
  };

  const handleToggle = (formType: string) => {
    setFormStates((prev) => {
      const next = { ...prev, [formType]: !prev[formType] };
      if (
        formType === 'Trip Authorization Request (TAR) Form' &&
        next[formType] === false
      ) {
        next['Trip Report (TR) Form'] = false;
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!config?.forms) return;
    const payload = {
      forms: config.forms.map((f) => ({
        formType: f.formType,
        enabled: formStates[f.formType] ?? f.enabled,
      })),
    };
    try {
      await updateFormVisibility(payload).unwrap();
      try {
        const formTypes = await refetchAvailableForms().unwrap();
        dispatch(setAvailableForms(formTypes.filter((f) => !f.deprecated)));
      } catch {
        // Navigation refresh failed — config was still saved successfully.
      }
      showNotification('Form visibility updated.');
    } catch (err) {
      const message =
        (err as { data?: { error?: string } })?.data?.error ??
        'Failed to update form visibility.';
      showNotification(message, 'error');
    }
  };

  const sortedForms = useMemo(() => {
    if (!config?.forms) return [];
    return [...config.forms].sort((a, b) =>
      a.formType.localeCompare(b.formType),
    );
  }, [config]);

  // Object types backed by an actual user-creatable form (the items under
  // the left-nav Forms group). Everything else is treated as another kind
  // of module (Archive, Deliverable Comment, Project Summary, etc.).
  const FORM_OBJECT_TYPES = new Set(['CTP', 'PNR', 'RIP', 'TAR', 'TR']);

  // Display-name overrides for noisy or awkwardly-phrased formType values
  // coming from the backend. Pure UI rename — doesn't touch the data.
  // (TAR also gets a correction here: the seed says "Trip Authorization
  // Request" but the acronym stands for "Travel Authorization Request".)
  const FORM_DISPLAY_NAMES: Record<string, string> = {
    'Consent To Purchase (CTP) Form': 'CTP - Consent To Purchase',
    'Problem Notification Report (PNR) Form':
      'PNR - Problem Notification Report',
    'Request to Initiate Purchase (RIP) Form':
      'RIP - Request to Initiate Purchase',
    'Trip Authorization Request (TAR) Form':
      'TAR - Travel Authorization Request',
    'Trip Report (TR) Form': 'TR - Trip Report',
    // Typo fix in the seed: "Deliverble" → "Deliverable".
    'Multi-Document Deliverble Form': 'Multi-Document Deliverable Form',
  };

  const { formItems, otherItems } = useMemo(() => {
    const formItems = sortedForms.filter((f) =>
      FORM_OBJECT_TYPES.has(f.objectType ?? ''),
    );
    const otherItems = sortedForms.filter(
      (f) => !FORM_OBJECT_TYPES.has(f.objectType ?? ''),
    );
    return { formItems, otherItems };
  }, [sortedForms]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ width: '100%', p: 2 }}>
        <Alert severity="error">Failed to load form visibility.</Alert>
      </Box>
    );
  }

  // Brand-blue primary action, matches other admin Save buttons.
  // minWidth keeps Save the same width as Update / Add Domain in SitewideSettings.
  const primaryActionSx = {
    borderRadius: 2,
    px: 2.5,
    py: 0.75,
    minWidth: 120,
    backgroundColor: '#1F4D83',
    '&:hover': { backgroundColor: '#163C66' },
    color: '#FFFFFF',
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: 'none',
    '&.Mui-disabled': { opacity: 0.6, color: '#FFFFFF' },
  };

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Module Visibility
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5, mb: 2 }}
        >
          Toggle which modules are available across the site. Disabled modules
          are hidden from navigation and creation menus.
        </Typography>

        {(() => {
          // TR rows are gated on TAR: Trip Reports must reference an approved
          // Trip Authorization Request, so disable the TR toggle when TAR is off.
          const tarForm = config?.forms?.find(
            (cf) => cf.formType === 'Trip Authorization Request (TAR) Form',
          );
          const isTarOff = !(
            formStates['Trip Authorization Request (TAR) Form'] ??
            tarForm?.enabled ??
            false
          );

          const renderRow = (
            f: (typeof sortedForms)[number],
            idx: number,
          ) => {
            const checked = formStates[f.formType] ?? f.enabled;
            const showObjectTypeChip =
              f.objectType && f.objectType !== f.formType;
            const isTrRow = f.formType === 'Trip Report (TR) Form';
            const gateTrRow = isTrRow && isTarOff;
            return (
              <Stack
                key={f.formType}
                direction="row"
                alignItems="center"
                spacing={2}
                title={
                  gateTrRow
                    ? 'Trip Reports must reference an approved Trip Authorization Request. Enable TAR before enabling TR.'
                    : undefined
                }
                sx={{
                  py: 1.25,
                  borderTop: idx === 0 ? 0 : 1,
                  borderColor: 'divider',
                  ...(gateTrRow ? { opacity: 0.5 } : {}),
                }}
              >
                <Typography variant="body1" sx={{ flex: 1 }}>
                  {FORM_DISPLAY_NAMES[f.formType] ?? f.formType}
                </Typography>
                {showObjectTypeChip && (
                  <Chip
                    label={f.objectType}
                    size="small"
                    variant="outlined"
                    sx={{ fontFamily: 'monospace', fontSize: 12 }}
                  />
                )}
                <Switch
                  checked={checked}
                  onChange={() => handleToggle(f.formType)}
                  disabled={gateTrRow}
                  data-testid={`form-visibility-checkbox-${f.formType}`}
                />
              </Stack>
            );
          };

          const groupHeading = (label: string, first: boolean) => (
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{
                display: 'block',
                mt: first ? 0 : 2,
                mb: 0.5,
                letterSpacing: 0.5,
                fontWeight: 600,
              }}
            >
              {label}
            </Typography>
          );

          return (
            <>
              {formItems.length > 0 && (
                <>
                  {groupHeading('Forms', true)}
                  <Box>{formItems.map(renderRow)}</Box>
                </>
              )}
              {otherItems.length > 0 && (
                <>
                  {groupHeading('Other Modules', formItems.length === 0)}
                  <Box>{otherItems.map(renderRow)}</Box>
                </>
              )}
            </>
          );
        })()}

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="small"
            disabled={isSaving}
            onClick={handleSave}
            data-testid="form-visibility-save"
            sx={primaryActionSx}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={notificationOpen}
        autoHideDuration={4000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleNotificationClose}
          severity={notificationSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notificationMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FormVisibility;
